import {
  GetAllResponse,
  CreateTodoInput,
  UpdateTodoInput,
  CreateFeedbackInput,
  UpdateFeedbackInput,
  GetFeedbackLogResponse,
} from "../../../types";

const API_URL = import.meta.env.VITE_API_URL;
const AUTH_URL = import.meta.env.VITE_AUTH_URL;

export const getToken = () => window?.localStorage?.getItem?.("auth") ?? undefined;
export const setToken = (token: string) => window.localStorage.setItem("auth", token);
export const clearToken = () => window.localStorage.removeItem("auth");

const authFetch = async (url: string, props?: RequestInit) => {
  const token = getToken();
  return fetch(url, {
    ...props,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { authorization: token } : {}),
      ...props?.headers,
    },
  });
};

// doridium's auth is a separate origin, hit directly — its /auth/* routes
// are already CORS-open. See ROADMAP.md for routing this same-origin later.
export const AuthApi = {
  login: async (input: { username: string; password: string }) => {
    const res = await fetch(`${AUTH_URL}/auth/login`, {
      method: "POST",
      body: JSON.stringify(input),
    });
    return res.json();
  },
  getUser: async (token: string) => {
    const res = await fetch(`${AUTH_URL}/auth/user`, {
      method: "GET",
      headers: { authorization: token },
    });
    return res.json();
  },
};

export const TodoApi = {
  getAll: async (): Promise<{
    success: boolean;
    data?: GetAllResponse;
    error?: string;
  }> => {
    const res = await authFetch(`${API_URL}/api/todo`);
    return res.json();
  },

  createTodo: async (input: CreateTodoInput) => {
    const res = await authFetch(`${API_URL}/api/todo`, {
      method: "POST",
      body: JSON.stringify(input),
    });
    return res.json();
  },

  updateTodo: async (input: UpdateTodoInput) => {
    const res = await authFetch(`${API_URL}/api/todo/${input.id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    return res.json();
  },

  deleteTodo: async (id: string, createdAt: string) => {
    const url = new URL(`${API_URL}/api/todo/${id}`);
    url.searchParams.set("createdAt", createdAt);
    const res = await authFetch(url.toString(), { method: "DELETE" });
    return res.json();
  },
};

export const FeedbackApi = {
  create: async (input: CreateFeedbackInput) => {
    const res = await authFetch(`${API_URL}/api/feedback`, {
      method: "POST",
      body: JSON.stringify(input),
    });
    return res.json();
  },

  update: async (input: UpdateFeedbackInput) => {
    const res = await authFetch(`${API_URL}/api/feedback/${input.id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    return res.json();
  },

  delete: async (id: string, createdAt: string, userId: string) => {
    const url = new URL(`${API_URL}/api/feedback/${id}`);
    url.searchParams.set("createdAt", createdAt);
    url.searchParams.set("userId", userId);
    const res = await authFetch(url.toString(), { method: "DELETE" });
    return res.json();
  },

  // Admin-only — quietly returns a non-success response for anyone else,
  // which the UI treats as "hide the feedback log" rather than an error.
  getLog: async (): Promise<{
    success: boolean;
    data?: GetFeedbackLogResponse;
    error?: string;
  }> => {
    const res = await authFetch(`${API_URL}/api/feedback`);
    return res.json();
  },
};
